import { useAtomCallback } from "jotai/utils";
import { useCallback, useState } from "react";
import { showChromeToolbarPlaybackControls } from "../classes/ChromeToolbarPlaybackControlsSingleton";
import { logger } from "../datadog/logger";
import { useIsZoomEnabled } from "../hooks";
import type { RoomDelegator } from "../video-sdk";
import { roomTokenAtom, useJoinRoom } from "../video-sdk";
import { zoomTokenAtom } from "../video-sdk/zoom";

export type ConnectRoomCallback = (
  displayName: string,
  options?: {
    tokenOverride?: string;
    onError?: (error: unknown) => void;
  },
) => Promise<RoomDelegator | undefined>;

/**
 * Returns a callback function that connects to a new Twilio room. This callback
 * will not set/handle any room listeners.
 */
export function useConnectRoomCallback(): [
  ConnectRoomCallback,
  { loading?: boolean },
] {
  const [loading, setLoading] = useState(false);
  const joinRoom = useJoinRoom();
  const [, checkZoomIsEnabled] = useIsZoomEnabled();
  const getRoomToken = useAtomCallback(
    useCallback(
      (get) => {
        return checkZoomIsEnabled() ? get(zoomTokenAtom) : get(roomTokenAtom);
      },
      [checkZoomIsEnabled],
    ),
  );

  const callback = useCallback(
    async function connectRoom(
      displayName: string,
      {
        tokenOverride,
        onError,
      }: {
        tokenOverride?: string;
        onError?: (error: unknown) => void;
      } = {},
    ) {
      const token = tokenOverride || getRoomToken();
      if (!token) return;
      setLoading(true);
      try {
        const room = await joinRoom(token, displayName);
        showChromeToolbarPlaybackControls();
        return room;
      } catch (error) {
        if (onError) {
          onError(error);
          return;
        }
        logger.error("Failed to connect to room", {}, error as Error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [callback, { loading }];
}
